import Badge, {
  type BadgeVariant,
} from "@/v2-backoffice-client/components/badge"
import {
  formatTrendPct,
  getTrendLevel,
  TREND_LABELS,
  TREND_TOOLTIP,
} from "@/v2-backoffice-client/usage/usage.constants"

export type UsageTrendBadgeProps = {
  trendPct: number | null | undefined
  /** Optional "X now vs Y previous" line prepended to the hover title. */
  detail?: string
  /**
   * Also spell the level out in the badge ("↓ −83% Falling hard"), the way
   * the score badge pairs its number with the level name. Off by default —
   * compact surfaces keep arrow + pct only.
   */
  showLevel?: boolean
  className?: string
}

type TrendLevel = ReturnType<typeof getTrendLevel>

const TREND_ARROWS: Record<TrendLevel, string> = {
  "falling-hard": "↓",
  falling: "↘",
  steady: "→",
  growing: "↗",
  new: "",
}

const TREND_VARIANTS: Record<TrendLevel, BadgeVariant> = {
  "falling-hard": "critical",
  falling: "warning",
  steady: "neutral",
  growing: "positive",
  new: "neutral",
}

// Activity trend for the last 28 days vs the 28 before (arrow + delta),
// tinted-pill palette shared with the score/churn badges.
function UsageTrendBadge(props: UsageTrendBadgeProps) {
  const { trendPct, detail, showLevel = false, className } = props
  const level = getTrendLevel(trendPct)
  const arrow = TREND_ARROWS[level]

  return (
    <Badge
      className={className}
      variant={TREND_VARIANTS[level]}
      tooltip={
        trendPct == null
          ? TREND_LABELS.new
          : `${TREND_LABELS[level]}${detail ? ` — ${detail}` : ""}. ${TREND_TOOLTIP}`
      }
    >
      {arrow && <span>{arrow}</span>}
      {formatTrendPct(trendPct)}
      {showLevel && <span className="font-normal">{TREND_LABELS[level]}</span>}
    </Badge>
  )
}

export default UsageTrendBadge
